import { twMerge } from 'tailwind-merge';
import React, { ButtonHTMLAttributes, MouseEventHandler } from 'react';
import BaseButton from './BaseButton';
import Icon, { IconKey } from './Icon';
import Loading from './Loading';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
    iconKey?: IconKey;
    color?: keyof typeof colors;
    size?: keyof typeof sizes;
    hideTextOnOnePanel?: boolean;
    onClick?: MouseEventHandler<HTMLButtonElement>;
    isLoading?: boolean;
}

// The ghost and danger styles draw their border with `ring-1`, a box-shadow,
// not with `outline`: the outline is the browser's focus ring, which the
// `focus-ring` on BaseButton shows on keyboard focus. Forced colours drop
// box-shadows, so there the border is an outline again.
export const colors = {
    basic: 'border-none',
    primary: 'bg-main text-textOnBrand border-none',
    secondary: 'bg-transparent hover:gray-200 border-none',
    gray: 'bg-gray-200 hover:darken-10 border-none',
    ghost: 'ring-main bg-transparent rounded-sm text-main ring-1 forced-colors:outline-1 disabled:bg-gray-100 disabled:text-gray-400 disabled:ring-gray-400',
    danger: 'rounded-sm bg-transparent text-red ring-1 forced-colors:outline-1 ring-red hover:bg-red hover:text-white',
    icon: 'rounded-sm text-gray-600',
    white: 'rounded-sm bg-white text-gray-600 border-none hover:bg-gray-200',
};

const sizes = {
    small: 'text-xs/5',
    default: 'px-2 py-2 text-sm/4',
};

const iconSize = {
    small: '1.25rem',
    default: '1.5rem',
};

const Button = ({
    className,
    color = 'basic',
    size = 'default',
    iconKey,
    onClick,
    children,
    disabled = false,
    hideTextOnOnePanel = false,
    isLoading,
    ...props
}: Props) => {
    if (iconKey && !children) {
        return (
            <BaseButton
                className={twMerge(
                    'flex size-8 items-center justify-center',
                    colors[color],
                    disabled ? 'cursor-default' : '',
                    className,
                )}
                disabled={disabled}
                onClick={onClick}
                {...props}
            >
                <Icon iconKey={iconKey} />
            </BaseButton>
        );
    }

    return (
        <BaseButton
            className={twMerge(
                'relative font-medium',
                colors[color],
                sizes[size],
                disabled ? 'cursor-default' : 'hover:darken-10',
                className,
                !hideTextOnOnePanel ? 'min-w-[120px]' : '',
            )}
            disabled={disabled}
            onClick={onClick}
            {...props}
        >
            {iconKey ? (
                <div className="flex items-center gap-x-2 pe-1">
                    <Icon iconKey={iconKey} size={iconSize[size]} />
                    <span
                        className={twMerge(
                            hideTextOnOnePanel && 'hidden sm:block',
                        )}
                    >
                        {children}
                    </span>
                </div>
            ) : (
                children
            )}
            {isLoading && (
                <div className="absolute end-4">
                    <Loading size={16} disableTimeout={true} />
                </div>
            )}
        </BaseButton>
    );
};

export default Button;
